import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import authService from '../services/authService'
import restaurantStaffService from '../services/restaurantStaffService'
import { AppDispatch, RootState } from '../store'
import { clearUser } from '../store/authSlice'
import { useTranslation } from '../hooks/use-i18n'

const Account: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { user } = useSelector((state: RootState) => state.auth)
  const [pendingInvites, setPendingInvites] = useState(0)

  useEffect(() => {
    restaurantStaffService
      .getMyInvitations()
      .then((invites) => setPendingInvites(invites.filter((i) => i.status === 'pending').length))
      .catch(() => setPendingInvites(0))
  }, [])

  const handleLogout = () => {
    authService.logout()
    dispatch(clearUser())
    navigate('/')
    toast.success(t('nav.signOut'))
  }

  return (
    <div className="min-h-screen">
      <main className="max-w-2xl mx-auto px-3 sm:px-4 lg:px-8 py-6 sm:py-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6">{t('nav.account')}</h1>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 mb-6 text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-blue-500/20 text-3xl">
            👤
          </div>
          <p className="text-xl font-bold text-white">{user?.name || t('account.userFallback')}</p>
          <p className="text-white/60 mt-1">{user?.email || t('account.emailFallback')}</p>
          <span className="inline-block mt-3 rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-semibold text-emerald-400">
            {t('account.active')}
          </span>
        </div>

        <div className="space-y-3">
          <Link
            to="/staff-invitations"
            className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition-colors"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-500/15 text-xl">✉️</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white">{t('nav.invitations')}</p>
              <p className="text-sm text-white/60">{t('account.invitationsDesc')}</p>
            </div>
            {pendingInvites > 0 ? (
              <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] px-1.5 flex items-center justify-center">
                {pendingInvites}
              </span>
            ) : null}
            <span className="text-white/40">›</span>
          </Link>

          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 hover:bg-white/10 transition-colors text-left"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-xl">🚪</div>
            <div>
              <p className="font-semibold text-white">{t('nav.signOut')}</p>
              <p className="text-sm text-white/60">{t('account.signOutDesc')}</p>
            </div>
          </button>
        </div>
      </main>
    </div>
  )
}

export default Account
